import { useState, useEffect } from 'react';

export function BulkAssignmentModal({ 
  sessions, // List of all sessions for workload calculation
  selectedSessions, // Array of selected session IDs
  selectedCount, // Number of selected sessions
  assignableUsers, // List of scribes
  onClose, 
  onAssign 
}) {
  const [selectedScribeId, setSelectedScribeId] = useState('');
  const [scribeWorkloads, setScribeWorkloads] = useState({});
  const [isAssigning, setIsAssigning] = useState(false);

  // Calculate workload for each scribe
  useEffect(() => {
    if (assignableUsers.length > 0 && sessions.length > 0) {
      const workloads = {};
      
      assignableUsers.forEach(scribe => {
        // Count active tasks for each scribe
        const taskCount = sessions.filter(s => {
          // Check by name (for existing assignments)
          const byName = s.assignedTo !== '-' && s.assignedTo === scribe.name;
          
          // Check by ID (if available)
          const byId = s.assignedToId && s.assignedToId === scribe.userId;
          
          // Check if task is still active (not finalized/approved/signed)
          const isActive = !s.status.includes('Finalized') && 
                          !s.status.includes('Approved') && 
                          !s.status.includes('Signed');
          
          return (byName || byId) && isActive;
        }).length;
        
        workloads[scribe.userId] = taskCount;
      });
      
      setScribeWorkloads(workloads);
    }
  }, [assignableUsers, sessions]);

  const handleSubmit = async () => {
    if (!selectedScribeId) return;
    
    setIsAssigning(true);
    try {
      // Find the selected scribe
      const selectedScribe = assignableUsers.find(s => s.userId === selectedScribeId);
      if (!selectedScribe) return;
      
      // Call the onAssign function with the selected scribe ID
      await onAssign(selectedSessions, selectedScribeId, selectedScribe.name);
    } finally {
      setIsAssigning(false);
    }
  };

  const getScribeTaskCount = (scribeId) => {
    return scribeWorkloads[scribeId] || 0;
  };

  const getAvailabilityStatus = (scribe) => {
    const taskCount = getScribeTaskCount(scribe.userId);
    const capacity = scribe.capacity || 50;
    const capacityPercent = (taskCount / capacity) * 100;
    
    if (scribe.availability === 'Unavailable') {
      return { 
        text: 'Unavailable', 
        color: 'bg-red-100 text-red-800 border-red-200',
        description: 'Not accepting new tasks'
      };
    }
    
    const totalAfterAssignment = taskCount + selectedCount;
    const newCapacityPercent = (totalAfterAssignment / capacity) * 100;
    
    if (newCapacityPercent >= 100) {
      return { 
        text: 'Overloaded', 
        color: 'bg-red-100 text-red-800 border-red-200',
        description: `Will have ${totalAfterAssignment} tasks (exceeds capacity)`
      };
    } else if (newCapacityPercent >= 80) {
      return { 
        text: 'Very Busy', 
        color: 'bg-red-100 text-red-800 border-red-200',
        description: `Will have ${totalAfterAssignment} tasks (${newCapacityPercent.toFixed(0)}% capacity)`
      };
    } else if (newCapacityPercent >= 60) {
      return { 
        text: 'Busy', 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        description: `Will have ${totalAfterAssignment} tasks (${newCapacityPercent.toFixed(0)}% capacity)`
      };
    } else if (newCapacityPercent >= 40) {
      return { 
        text: 'Moderate', 
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        description: `Will have ${totalAfterAssignment} tasks (${newCapacityPercent.toFixed(0)}% capacity)`
      };
    } else if (newCapacityPercent >= 20) {
      return { 
        text: 'Available', 
        color: 'bg-green-100 text-green-800 border-green-200',
        description: `Will have ${totalAfterAssignment} tasks (${newCapacityPercent.toFixed(0)}% capacity)`
      };
    } else {
      return { 
        text: 'Highly Available', 
        color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        description: `Will have ${totalAfterAssignment} tasks (${newCapacityPercent.toFixed(0)}% capacity)`
      };
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="max-w-2xl w-full mx-auto">
        {/* Modal Container */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-2xl font-bold">Bulk Assign Sessions</h3>
                <div className="mt-2">
                  <p className="text-blue-100">
                    <span className="font-medium">{selectedCount} sessions</span> selected for assignment
                  </p>
                  <p className="text-blue-200 text-sm mt-1">
                    All selected sessions will be assigned to a single scribe
                  </p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {/* Selection Info */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-blue-800">
                    Ready to assign <span className="font-bold">{selectedCount}</span> sessions
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    All selected sessions will be assigned to the scribe you choose below
                  </p>
                </div>
              </div>
            </div>

            {/* Scribe Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800">Select Scribe</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Choose one scribe to receive all selected sessions
                  </p>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-4 text-left w-12"></th>
                        <th className="p-4 text-left text-sm font-semibold text-gray-700">Scribe</th>
                        <th className="p-4 text-left text-sm font-semibold text-gray-700">Current Workload</th>
                        <th className="p-4 text-left text-sm font-semibold text-gray-700">After Assignment</th>
                        <th className="p-4 text-left text-sm font-semibold text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {assignableUsers.length > 0 ? (
                        assignableUsers.map((scribe) => {
                          const taskCount = getScribeTaskCount(scribe.userId);
                          const capacity = scribe.capacity || 15;
                          const availability = getAvailabilityStatus(scribe);
                          
                          return (
                            <tr 
                              key={scribe.userId}
                              className={`cursor-pointer transition-all hover:bg-gray-50 ${
                                selectedScribeId === scribe.userId ? 'bg-blue-50 ring-1 ring-blue-300' : ''
                              } ${scribe.availability === 'Unavailable' ? 'opacity-60 cursor-not-allowed' : ''}`}
                              onClick={() => scribe.availability !== 'Unavailable' && setSelectedScribeId(scribe.userId)}
                            >
                              <td className="p-4">
                                <div className="flex items-center justify-center">
                                  <input 
                                    type="radio" 
                                    name="bulkScribe"
                                    checked={selectedScribeId === scribe.userId}
                                    onChange={() => scribe.availability !== 'Unavailable' && setSelectedScribeId(scribe.userId)}
                                    disabled={scribe.availability === 'Unavailable'}
                                    className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500"
                                  />
                                </div>
                              </td>
                              <td className="p-4">
                                <div>
                                  <div className="font-medium text-gray-900">{scribe.name}</div>
                                  <div className="text-sm text-gray-500">{scribe.email}</div>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="space-y-1">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Active Tasks:</span>
                                    <span className="font-medium">{taskCount}</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${
                                        (taskCount / capacity * 100) >= 80 ? 'bg-red-500' :
                                        (taskCount / capacity * 100) >= 60 ? 'bg-yellow-500' :
                                        (taskCount / capacity * 100) >= 40 ? 'bg-blue-500' :
                                        (taskCount / capacity * 100) >= 20 ? 'bg-green-500' :
                                        'bg-emerald-500'
                                      }`}
                                      style={{ width: `${Math.min((taskCount / capacity) * 100, 100)}%` }}
                                    ></div>
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {Math.round((taskCount / capacity) * 100)}% of capacity
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="space-y-1">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Total Tasks:</span>
                                    <span className="font-medium">{taskCount + selectedCount}</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${
                                        ((taskCount + selectedCount) / capacity * 100) >= 100 ? 'bg-red-600' :
                                        ((taskCount + selectedCount) / capacity * 100) >= 80 ? 'bg-red-500' :
                                        ((taskCount + selectedCount) / capacity * 100) >= 60 ? 'bg-yellow-500' :
                                        ((taskCount + selectedCount) / capacity * 100) >= 40 ? 'bg-blue-500' :
                                        ((taskCount + selectedCount) / capacity * 100) >= 20 ? 'bg-green-500' :
                                        'bg-emerald-500'
                                      }`}
                                      style={{ width: `${Math.min(((taskCount + selectedCount) / capacity) * 100, 100)}%` }}
                                    ></div>
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {Math.round(((taskCount + selectedCount) / capacity) * 100)}% of capacity
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="space-y-1">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${availability.color}`}>
                                    {availability.text}
                                  </span>
                                  <div className="text-xs text-gray-500 max-w-[120px]">
                                    {availability.description}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-gray-500">
                            <div className="flex flex-col items-center gap-2">
                              <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
                              </svg>
                              <p>No scribes available for assignment</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Selected Scribe Summary */}
              {selectedScribeId && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-green-800">
                        Selected: <span className="font-bold">
                          {assignableUsers.find(s => s.userId === selectedScribeId)?.name}
                        </span>
                      </p>
                      <p className="text-sm text-green-600 mt-1">
                        Will receive {selectedCount} sessions • Current tasks: {
                          getScribeTaskCount(selectedScribeId)
                        } • Total after: {
                          getScribeTaskCount(selectedScribeId) + selectedCount
                        }
                      </p>
                    </div>
                    <div className="text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full font-medium">
                      Ready to assign
                    </div>
                  </div>
                </div>
              )}

              {/* Information Panel */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-800">Bulk Assignment Notes</p>
                    <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                      <li>All selected sessions will be assigned to the chosen scribe</li>
                      <li>Standard 2-day due date applies to each session</li>
                      <li>Scribe will receive notification for all assignments</li>
                      <li>Workload calculation includes current active tasks</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {selectedScribeId ? (
                  <span>
                    Assigning {selectedCount} sessions to{' '}
                    <span className="font-medium text-gray-800">
                      {assignableUsers.find(s => s.userId === selectedScribeId)?.name}
                    </span>
                  </span>
                ) : (
                  <span>Select a scribe to continue</span>
                )}
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={onClose}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSubmit}
                  disabled={!selectedScribeId || isAssigning}
                  className={`px-6 py-2.5 font-medium rounded-lg transition-all flex items-center gap-2 ${
                    !selectedScribeId || isAssigning
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-sm'
                  }`}
                >
                  {isAssigning ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Assigning...
                    </>
                  ) : (
                    `Assign ${selectedCount} Sessions`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}